/**
 * dialogue_threads.ts — PURE engine for versona DIALOGUE PATHS (2026-09-20).
 *
 * The topic window (topic_dialogue.ts) is Morrowind: the player asks, the
 * versona answers, one line at a time. It never takes the lead, never asks
 * anything back, never remembers what was said, and nothing learned from one
 * versona matters to another. This module adds the structures the research
 * (docs/research/2026-09-20-dialogue-paths-research.md) found everywhere else:
 *
 *   OPENERS      the versona brings something up — a Hades-style bucket:
 *                essential > contextual > evergreen, most-criteria-wins inside
 *                a tier (Valve's fuzzy match), UNSEEN before seen.
 *   QUESTIONS    they ask about YOU; the answers build a Traveler Persona
 *                (Kentucky Route Zero: choices define the character, not the
 *                plot), are remembered (Pentiment) and called back (Firewatch);
 *                walking away from a question is itself remembered.
 *   LIFE STORY   a procedural biography told one chapter a visit, gated by the
 *                relationship tier (Hades arcs / Stardew heart events).
 *   TALES        they ask for a tale of a MOOD; you tell one you gathered from
 *                someone else (Where the Water Tastes Like Wine; Qud's secrets
 *                as inventory) and they give one back.
 *   PROVENANCE   "Who told you that?" walks the real `memory_provenance`
 *                chain teller by teller to the witness (Obra Dinn's
 *                cross-reference; Dwarf Fortress's "they only know what they
 *                were told").
 *   LEADS        what you learn becomes askable elsewhere, inside Heaven's
 *                Vault's WINDOW: known enough to pose, not so resolved that
 *                asking is redundant.
 *   STANCE       how you say it matters, by THEIR personality (Alpha
 *                Protocol / Kingdom Come): the bold respect blunt, the gentle
 *                flinch from it.
 *
 * Everything here is a pure function of (facts, memory): no DOM, no clock, no
 * storage — `nowDay` rides in on the facts and the caller persists TalkMemory.
 */

import { fillLine, type TopicFacts } from './topic_dialogue.js';

// ─── Facts ───────────────────────────────────────────────────────────────────

/** A rumor this versona carries, with where it came from (`memory_provenance`). */
export interface RumorFact {
  memoryId: string; text: string; factKey: string;
  tellerId: string; tellerName: string; tellerTown: string;
  hops: number; confidence: number;
  /** A planted tale (a traveler's, a player's): teller 0 AND origin 0 — "someone said". */
  planted: boolean;
}
/** Something they saw with their own eyes. */
export interface WitnessFact { memoryId: string; text: string; factKey: string; importance: number }

export interface ThreadFacts extends TopicFacts {
  versonaId: string;
  settlementId: string;
  /** npc_agent personality, 0..100. */
  boldness: number; greed: number; diligence: number; sociability: number;
  /** versona_opinion redistribution lean ×10 (+ welfare / higher levy, − low levy); null = unknown. */
  leanX10: number | null;
  /** Birthplace (versona_origin): '' unknown. */
  bornTown: string;
  migrant: boolean;
  /** Their trade TITLE (highest practised skill): '' none. */
  title: string;
  rumorFacts: RumorFact[];
  witnessFacts: WitnessFact[];
  /** Unix day — the ONLY clock the engine sees (cooldowns, "yesterday"). */
  nowDay: number;
}

// ─── Memory (serializable; the caller persists it) ───────────────────────────

export type PersonaSlot = 'origin' | 'trade' | 'purpose' | 'warView' | 'rivalView' | 'townView';
export const PERSONA_VALUES: Record<PersonaSlot, readonly string[]> = {
  origin: ['spacer', 'commons', 'frontier', 'secret'],
  trade: ['trader', 'pilot', 'fighter', 'crafter', 'wanderer'],
  purpose: ['work', 'curiosity', 'trouble', 'someone'],
  warView: ['hawk', 'dove', 'neutral'],
  rivalView: ['open', 'wary'],
  townView: ['fond', 'plain', 'sour'],
};

export type TaleMood = 'hopeful' | 'brave' | 'funny' | 'sad' | 'strange' | 'justice';
export interface Tale {
  id: string; text: string; mood: TaleMood;
  /** Who gave it to you, and where. */
  from: string; fromId: string; fromTown: string;
  day: number;
  /** First-hand from a witness (a traced rumor, a life chapter) — worth more in the telling. */
  firstHand: boolean;
  /** versonaIds you have already told it to. */
  toldTo: string[];
}

export type LeadState = 'open' | 'resolved' | 'cold';
export interface Lead {
  id: string; factKey: string; text: string;
  /** Who to ask NEXT. */
  targetId: string; targetName: string; targetTown: string;
  /** Names already walked, oldest first. */
  trail: string[];
  openedDay: number;
  state: LeadState;
}

export interface VersonaTalk {
  visits: number; lastVisitDay: number;
  /** Thread ids already played with THIS versona (Hades: unseen before seen). */
  seen: string[];
  chapter: number; lastChapterDay: number;
  /** Question slots they asked → the day; an entry with no `told` answer is an IGNORED question. */
  asked: Partial<Record<PersonaSlot, number>>;
  /** What you told THIS versona about yourself. */
  told: Partial<Record<PersonaSlot, string>>;
  /** Issue → where you stood with them. */
  opined: Record<string, 'agree' | 'disagree' | 'dodge'>;
  lastTaleDay: number;
}
export interface TalkMemory {
  v: 1;
  /** The Traveler Persona — your LATEST answer per slot (you may tell different people different things). */
  persona: Partial<Record<PersonaSlot, string>>;
  /** Per settlement: who you first told each slot to, and when — how word of you gets round a town. */
  toldInTown: Record<string, Partial<Record<PersonaSlot, { by: string; byName: string; day: number; value: string }>>>;
  versonas: Record<string, VersonaTalk>;
  tales: Tale[];
  leads: Lead[];
}

export const TALE_CAP = 24;
export const LEAD_CAP = 16;
export const SEEN_CAP = 40;
export const VERSONA_CAP = 120;

export function emptyTalkMemory(): TalkMemory {
  return { v: 1, persona: {}, toldInTown: {}, versonas: {}, tales: [], leads: [] };
}
export function emptyVersonaTalk(): VersonaTalk {
  return { visits: 0, lastVisitDay: 0, seen: [], chapter: 0, lastChapterDay: 0, asked: {}, told: {}, opined: {}, lastTaleDay: 0 };
}
/** Defensive load: anything not shaped like v1 is a fresh memory (never throw on a stale blob). */
export function reviveTalkMemory(raw: unknown): TalkMemory {
  const m = raw as Partial<TalkMemory> | null;
  if (!m || m.v !== 1 || typeof m.versonas !== 'object' || !Array.isArray(m.tales) || !Array.isArray(m.leads)) return emptyTalkMemory();
  return { v: 1, persona: m.persona ?? {}, toldInTown: m.toldInTown ?? {}, versonas: m.versonas ?? {}, tales: m.tales, leads: m.leads };
}
export function talkOf(m: TalkMemory, versonaId: string): VersonaTalk {
  return m.versonas[versonaId] ?? emptyVersonaTalk();
}
function withTalk(m: TalkMemory, versonaId: string, patch: Partial<VersonaTalk>): TalkMemory {
  const next = { ...talkOf(m, versonaId), ...patch };
  const versonas = { ...m.versonas, [versonaId]: next };
  // Bound the map: the least recently visited go first.
  const ids = Object.keys(versonas);
  if (ids.length > VERSONA_CAP) {
    ids.sort((a, b) => versonas[a].lastVisitDay - versonas[b].lastVisitDay);
    for (const id of ids.slice(0, ids.length - VERSONA_CAP)) if (id !== versonaId) delete versonas[id];
  }
  return { ...m, versonas };
}

/** A conversation began. */
export function noteVisit(m: TalkMemory, f: ThreadFacts): TalkMemory {
  const t = talkOf(m, f.versonaId);
  return withTalk(m, f.versonaId, { visits: t.visits + 1, lastVisitDay: f.nowDay });
}

// ─── Hash (deterministic variety, never Math.random) ─────────────────────────

export function hash32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return h >>> 0;
}
const pick = <T>(arr: readonly T[], seed: string): T => arr[hash32(seed) % arr.length];

// ─── Threads: beats, replies, effects ────────────────────────────────────────

export type Tone = 'warm' | 'plain' | 'blunt' | 'wry';

export type Effect =
  | { t: 'persona'; slot: PersonaSlot; value: string }
  | { t: 'opine'; issue: string; stance: 'agree' | 'disagree' | 'dodge' }
  | { t: 'discover'; topic: string }
  /** "Who told you that?" — the module answers from the rumor's provenance and opens a lead. */
  | { t: 'source' }
  | { t: 'chapter' };

export interface Reply {
  label: string;
  tone?: Tone;
  /** Next beat id; absent = the thread ends after the versona's reaction. */
  goto?: string;
  effects?: Effect[];
}
export interface Beat { id: string; say: string; replies?: Reply[] }

export type ThreadKind = 'opener' | 'question' | 'opinion' | 'callback' | 'confront' | 'life';
/** A view of memory the criteria may read. */
export interface MemView { me: VersonaTalk; all: TalkMemory }
export type ThreadCriterion = (f: ThreadFacts, m: MemView) => boolean;

export interface ThreadDef {
  id: string;
  kind: ThreadKind;
  /** 1 essential · 2 contextual · 3 evergreen (the Hades bucket). */
  tier: 1 | 2 | 3;
  /** ALL must pass; the COUNT is the specificity (most-criteria-wins, so split conjunctions). */
  when: ThreadCriterion[];
  /** Never again with this versona once played. Otherwise it may recur after everything unseen is spent. */
  once?: boolean;
  /** For a question thread: the persona slot it fills (so an unanswered one can be recalled). */
  asks?: PersonaSlot;
  beats: Beat[];
}

const TIER_ORDER = ['nemesis', 'hostile', 'cold', 'stranger', 'familiar', 'friend', 'confidant'] as const;
export function tierRank(t: TopicFacts['relTier'] | undefined): number { return TIER_ORDER.indexOf(t ?? 'stranger'); }
export const tierAtLeast = (min: (typeof TIER_ORDER)[number]): ThreadCriterion => (f) => tierRank(f.relTier) >= tierRank(min);
export const tierBelow = (max: (typeof TIER_ORDER)[number]): ThreadCriterion => (f) => tierRank(f.relTier) < tierRank(max);

/**
 * THE OPENER — what, if anything, the versona brings up after the greeting.
 *
 *   1. criteria filter   (a thread whose `once` was spent is gone for good)
 *   2. lowest TIER wins  (essential before contextual before evergreen)
 *   3. UNSEEN before seen (Hades: spend the unused before anything repeats)
 *   4. most CRITERIA     (Valve: the most specific line that fits)
 *   5. a stable hash     (the same versona on the same day picks the same one)
 *
 * Evergreen chatter is rationed (not every hello is an interview): a tier-3
 * thread only opens on roughly two visits in three, by the same hash.
 *
 * ORDER LAW: call this BEFORE `noteVisit` for the conversation that is opening.
 * `visits` and `lastVisitDay` then describe the PAST — "have we talked before,
 * and was it today?" — which is what every callback criterion means to ask.
 * Noting the visit first makes every return look like a first meeting's twin.
 */
export function pickOpener(bank: readonly ThreadDef[], f: ThreadFacts, mem: TalkMemory): ThreadDef | null {
  const me = talkOf(mem, f.versonaId);
  const view: MemView = { me, all: mem };
  const seen = new Set(me.seen);
  const fits = bank.filter((th) => {
    if (th.kind === 'life') return false; // chapters are asked for, never volunteered
    if (th.once && seen.has(th.id)) return false;
    for (const c of th.when) if (!c(f, view)) return false;
    return true;
  });
  if (fits.length === 0) return null;
  const score = (th: ThreadDef): [number, number, number, number] =>
    [th.tier, seen.has(th.id) ? 1 : 0, -th.when.length, hash32(`${f.versonaId}|${f.nowDay}|${th.id}`)];
  fits.sort((a, b) => { const x = score(a), y = score(b); for (let i = 0; i < 4; i++) if (x[i] !== y[i]) return x[i] - y[i]; return 0; });
  const best = fits[0];
  if (best.tier === 3 && hash32(`${f.versonaId}|${f.nowDay}|${me.visits}|gate`) % 3 === 0) return null;
  // A SEEN repeatable only returns once a day has passed — never the same opener twice in one sitting.
  if (seen.has(best.id) && me.lastVisitDay === f.nowDay) return null;
  return best;
}

export function beatOf(th: ThreadDef, id: string): Beat | null { return th.beats.find((b) => b.id === id) ?? null; }

/** Record that a thread played (and, for a question, that it was ASKED — answered or not). */
export function noteThread(m: TalkMemory, f: ThreadFacts, th: ThreadDef): TalkMemory {
  const t = talkOf(m, f.versonaId);
  const seen = t.seen.includes(th.id) ? t.seen : [...t.seen, th.id].slice(-SEEN_CAP);
  const asked = th.asks && t.asked[th.asks] === undefined ? { ...t.asked, [th.asks]: f.nowDay } : t.asked;
  return withTalk(m, f.versonaId, { seen, asked });
}

/** Apply a reply's effects to memory. Returns the new memory and whether anything WORTH REMEMBERING happened. */
export function applyEffects(m: TalkMemory, f: ThreadFacts, effects: readonly Effect[] | undefined): { mem: TalkMemory; remembered: boolean } {
  let mem = m, remembered = false;
  for (const e of effects ?? []) {
    if (e.t === 'persona') {
      if (!PERSONA_VALUES[e.slot].includes(e.value)) continue; // a closed vocabulary — the server composes text from it
      const t = talkOf(mem, f.versonaId);
      mem = withTalk(mem, f.versonaId, { told: { ...t.told, [e.slot]: e.value } });
      const town = { ...(mem.toldInTown[f.settlementId] ?? {}) };
      if (!town[e.slot]) town[e.slot] = { by: f.versonaId, byName: f.name, day: f.nowDay, value: e.value };
      mem = { ...mem, persona: { ...mem.persona, [e.slot]: e.value }, toldInTown: { ...mem.toldInTown, [f.settlementId]: town } };
      remembered = true;
    } else if (e.t === 'opine') {
      const t = talkOf(mem, f.versonaId);
      mem = withTalk(mem, f.versonaId, { opined: { ...t.opined, [e.issue]: e.stance } });
      if (e.stance !== 'dodge') remembered = true;
    } else if (e.t === 'chapter') {
      const t = talkOf(mem, f.versonaId);
      mem = withTalk(mem, f.versonaId, { chapter: t.chapter + 1, lastChapterDay: f.nowDay });
    }
  }
  return { mem, remembered };
}

/** A question left hanging: the player changed the subject (Firewatch: silence is an answer too). */
export function ignoredSlots(me: VersonaTalk): PersonaSlot[] {
  return (Object.keys(me.asked) as PersonaSlot[]).filter((s) => me.told[s] === undefined);
}

/**
 * WORD GETS ROUND. What you told one versona reaches the sociable others of
 * the same town a day later — they did not hear it from you.
 */
export function heardAbout(f: ThreadFacts, mem: TalkMemory, slot: PersonaSlot): { value: string; byName: string } | null {
  const rec = mem.toldInTown[f.settlementId]?.[slot];
  if (!rec || rec.by === f.versonaId) return null;
  if (f.nowDay - rec.day < 1) return null;
  if (f.sociability < 40) return null; // the ones who keep to themselves have not heard
  return { value: rec.value, byName: rec.byName };
}

// ─── Stance: how a TONE lands on THIS personality ────────────────────────────

export type Landing = 'lands' | 'neutral' | 'grates';
export function toneLanding(tone: Tone | undefined, f: ThreadFacts): Landing {
  if (!tone) return 'neutral';
  const bold = f.boldness >= 60, gentle = f.boldness < 40;
  const sociable = f.sociability >= 60, busy = f.diligence >= 60 && (f.state === 'working' || f.state === 'building' || f.state === 'trading');
  switch (tone) {
    case 'blunt': return bold ? 'lands' : gentle ? 'grates' : 'neutral';
    case 'warm': return gentle || sociable ? 'lands' : 'neutral';
    case 'wry': return busy ? 'grates' : sociable || bold ? 'lands' : 'neutral';
    case 'plain': return busy ? 'lands' : 'neutral';
  }
}
const LANDING_LINES: Record<Landing, Record<Tone, string[]>> = {
  lands: {
    blunt: ['Ha. Straight to it — I can work with that.', 'Good. No one wastes my time by being honest.'],
    warm: ['That\'s kindly said. Not everyone bothers.', 'Mm. You\'re easy company, %player%.'],
    wry: ['Ha! You\'ll fit in here, saying things like that.', 'That got a laugh out of me, and I wasn\'t planning on one today.'],
    plain: ['Short and clear. My kind of answer, mid-shift.', 'Right. That\'s all I needed.'],
  },
  neutral: {
    blunt: ['Well. That\'s one way to put it.'], warm: ['Kind of you.'], wry: ['Hm. If you say so.'], plain: ['Fair enough.'],
  },
  grates: {
    blunt: ['…No need to put it quite like that.', 'You could say the same thing softer, you know.'],
    warm: ['Kind of you.'],
    wry: ['I\'m mid-shift. Jokes keep.', 'Save the wit for the canteen — I\'ve work in front of me.'],
    plain: ['Fair enough.'],
  },
};
/** The versona's reaction to HOW you said it (appended after a reply that carries a tone). */
export function landingLine(tone: Tone | undefined, f: ThreadFacts): string {
  if (!tone) return '';
  const l = toneLanding(tone, f);
  return fillLine(pick(LANDING_LINES[l][tone], `${f.versonaId}|${f.nowDay}|${tone}|${l}`), f);
}

// ─── Tales: moods, the satchel, the swap ─────────────────────────────────────

const MOOD_WORDS: Array<[TaleMood, RegExp]> = [
  ['sad', /\b(died|dead|death|grave|buried|lost|mourn|grief|widow|starv|famine|fell at|killed|funeral)\b/i],
  ['justice', /\b(tribunal|judged|fined|thief|stole|cheat|crime|guilty|sentence|exile|caught|swindl|levy|court)\b/i],
  ['brave', /\b(fought|drove off|stood|defended|battle|charge|held the|rescued|saved|hunter|beast|raider|siege|war)\b/i],
  ['hopeful', /\b(born|wed|married|harvest|recover|healed|rebuilt|festival|feast|peace|returned home|reunited|good year)\b/i],
  ['funny', /\b(laugh|drunk|fell in|goat|chased|wrong door|slipped|bet|snor|trousers|prank)\b/i],
  ['strange', /\b(lights|signal|void|wormhole|vanish|nobody knows|ghost|whisper|they say|strange|unexplained|derelict)\b/i],
];
export function moodOf(text: string): TaleMood {
  for (const [mood, re] of MOOD_WORDS) if (re.test(text)) return mood;
  return 'strange';
}

const ADJACENT: Record<TaleMood, TaleMood[]> = {
  hopeful: ['funny'], funny: ['hopeful'], brave: ['justice'], justice: ['brave'], sad: ['strange'], strange: ['sad'],
};

/** What kind of tale they want to hear right now — read off their state, never random. */
export function taleWanted(f: ThreadFacts): TaleMood {
  if (f.feelingTone === 'down') return 'hopeful';
  if (f.feelingTone === 'uneasy') return 'brave';
  if (f.feelingTone === 'angry') return 'justice';
  if (f.atWar) return 'brave';
  if (f.feelingTone === 'glad' || f.happy) return 'funny';
  if (f.tod === 'night' || f.venueFn === 'entertainment') return 'strange';
  return pick(['funny', 'strange', 'brave', 'hopeful'] as const, `${f.versonaId}|${f.nowDay}|want`);
}
const WANT_LINES: Record<TaleMood, string[]> = {
  hopeful: ['I could use something with a good ending, truth be told. Do you carry any tale like that?', 'Tell me something that turned out well. I\'ve had enough of the other kind.'],
  brave: ['Give me a tale where somebody stood their ground. We could use the example.', 'Tell me about someone with more nerve than sense. Those are the ones worth hearing.'],
  justice: ['Tell me one where somebody got what they had coming. I\'m in the mood for it.', 'Do you know a tale where the books got balanced? A cheat caught, a wrong put right?'],
  funny: ['Go on, make me laugh. You must have heard something ridiculous on the road.', 'Tell me something foolish. The day\'s been too sensible by half.'],
  strange: ['Tell me something odd. The kind of thing nobody can quite explain.', 'You travel. You must have heard something strange out there — I collect those.'],
  sad: ['Tell me a sad one, if you have it. Some days it helps to hear you\'re not the only one.'],
};
export function taleAskLine(f: ThreadFacts): string {
  return fillLine(pick(WANT_LINES[taleWanted(f)], `${f.versonaId}|${f.nowDay}|ask`), f);
}

export type TaleVerdict = 'delighted' | 'pleased' | 'polite' | 'heard-it' | 'thats-mine';
export function judgeTale(tale: Tale, f: ThreadFacts): TaleVerdict {
  if (tale.fromId === f.versonaId) return 'thats-mine';
  if (tale.toldTo.includes(f.versonaId)) return 'heard-it';
  const want = taleWanted(f);
  if (tale.mood === want) return 'delighted';
  if (ADJACENT[want].includes(tale.mood)) return 'pleased';
  return 'polite';
}
const VERDICT_LINES: Record<TaleVerdict, string[]> = {
  delighted: ['Now THAT is the tale I needed. I\'ll be telling that one myself — you watch.', 'Ha — yes. That\'s exactly it. Thank you, %player%. That one\'s going round the canteen tonight.'],
  pleased: ['Not what I asked for, quite — but a good one. I\'ll keep it.', 'Mm. That\'ll do nicely. Close enough to what I was after.'],
  polite: ['Huh. Well. It\'s a tale, I\'ll give you that.', 'I see. Not really my mood today — but thank you for the telling.'],
  'heard-it': ['You told me that one already. It was better the first time.', 'I know that one — from you, as it happens.'],
  'thats-mine': ['That\'s MY story. And you\'ve got the ending wrong, for what it\'s worth.', 'Ha — that\'s mine! So it IS getting around. You tell it worse than I do.'],
};
export function verdictLine(v: TaleVerdict, f: ThreadFacts): string {
  return fillLine(pick(VERDICT_LINES[v], `${f.versonaId}|${f.nowDay}|${v}`), f);
}
/** Did the telling earn a tale back (and the day's goodwill)? */
export const verdictEarns = (v: TaleVerdict): boolean => v === 'delighted' || v === 'pleased';

/** Strip the hearsay framing so a tale reads as a tale in the satchel. */
export function taleTextOf(text: string): string {
  return text.replace(/^(A traveler claims:|They say:?|Word is:?|I heard:?)\s*/i, '').trim();
}
function taleId(fromId: string, text: string): string { return `t${hash32(`${fromId}|${taleTextOf(text)}`).toString(36)}`; }

export function addTale(m: TalkMemory, t: Omit<Tale, 'id' | 'toldTo' | 'mood'> & { mood?: TaleMood }): TalkMemory {
  const text = taleTextOf(t.text);
  if (text.length < 12) return m;
  const id = taleId(t.fromId, text);
  if (m.tales.some((x) => x.id === id)) return m;
  const tale: Tale = { ...t, id, text, mood: t.mood ?? moodOf(text), toldTo: [] };
  return { ...m, tales: [...m.tales, tale].slice(-TALE_CAP) };
}
export function noteTaleTold(m: TalkMemory, taleIdStr: string, f: ThreadFacts): TalkMemory {
  const tales = m.tales.map((t) => (t.id === taleIdStr && !t.toldTo.includes(f.versonaId) ? { ...t, toldTo: [...t.toldTo, f.versonaId] } : t));
  return withTalk({ ...m, tales }, f.versonaId, { lastTaleDay: f.nowDay });
}
/** The tale they give back: the best thing they SAW (first-hand), else the freshest thing they heard. */
export function taleFromVersona(f: ThreadFacts, mem: TalkMemory): { text: string; firstHand: boolean } | null {
  const have = new Set(mem.tales.map((t) => t.id));
  const w = [...f.witnessFacts].sort((a, b) => b.importance - a.importance).find((x) => !have.has(taleId(f.versonaId, x.text)) && taleTextOf(x.text).length >= 12);
  if (w) return { text: w.text, firstHand: true };
  const r = f.rumorFacts.find((x) => !have.has(taleId(f.versonaId, x.text)) && taleTextOf(x.text).length >= 12);
  return r ? { text: r.text, firstHand: false } : null;
}
/** One swap a day per versona — a tale is a gift, not a vending machine. */
export function canSwapTale(f: ThreadFacts, mem: TalkMemory): boolean {
  return talkOf(mem, f.versonaId).lastTaleDay !== f.nowDay && tierRank(f.relTier) >= tierRank('cold');
}

// ─── Provenance: "Who told you that?" ────────────────────────────────────────

/** How sure they sound — confidence falls 15 a hop on the server; the words follow it. */
export function confidencePhrase(conf: number): string {
  if (conf >= 90) return 'I\'d swear to it';
  if (conf >= 70) return 'I believe it';
  if (conf >= 50) return 'I\'d salt it a little';
  return 'I wouldn\'t bet a credit on it';
}
export function sourceLine(r: RumorFact, f: ThreadFacts): string {
  if (r.planted) return 'A traveler said it, passing through. Couldn\'t tell you which one — they all look alike with road dust on them.';
  if (r.tellerId === '0' || r.tellerName === '') return 'Heard it at the canteen. Couldn\'t tell you from whom — it was just in the air, the way things are.';
  const where = r.tellerTown && r.tellerTown !== f.town ? ` — over in ${r.tellerTown}` : '';
  if (r.hops <= 1) return `I had it straight from ${r.tellerName}${where}, and ${r.tellerName} was THERE. ${cap(confidencePhrase(r.confidence))}.`;
  if (r.hops === 2) return `${r.tellerName} told me${where}. They had it from someone who saw it happen. ${cap(confidencePhrase(r.confidence))}.`;
  return `It\'s come through a few mouths by now. ${r.tellerName} told me${where}, for what that\'s worth — ${confidencePhrase(r.confidence)}.`;
}
const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** Asking for the source opens (or re-points) a lead at the teller. Planted / unknown tellers are dead ends. */
export function openLead(m: TalkMemory, r: RumorFact, f: ThreadFacts): { mem: TalkMemory; lead: Lead | null } {
  if (r.planted || r.tellerId === '0' || r.tellerName === '') return { mem: m, lead: null };
  const id = `l${hash32(r.factKey).toString(36)}`;
  const existing = m.leads.find((l) => l.id === id);
  if (existing && existing.state !== 'open') return { mem: m, lead: existing };
  const lead: Lead = {
    id, factKey: r.factKey, text: taleTextOf(r.text),
    targetId: r.tellerId, targetName: r.tellerName, targetTown: r.tellerTown,
    trail: existing ? existing.trail : [f.name], openedDay: existing?.openedDay ?? f.nowDay, state: 'open',
  };
  const leads = [...m.leads.filter((l) => l.id !== id), lead].slice(-LEAD_CAP);
  return { mem: { ...m, leads }, lead };
}

/**
 * THE WINDOW (Heaven's Vault): a lead is askable HERE only when you know
 * enough to pose it (it is open) and asking is not redundant (it is not
 * resolved), and this versona is the person the trail points at.
 */
export function leadsFor(f: ThreadFacts, mem: TalkMemory): Lead[] {
  return mem.leads.filter((l) => l.state === 'open' && l.targetId === f.versonaId);
}

export type LeadOutcome = 'resolved' | 'advanced' | 'cold';
/**
 * Put a lead to the versona it points at. They hold the story first-hand
 * (RESOLVED — you found the witness), or they had it from someone else
 * (ADVANCED — the trail moves on), or it has gone out of their head (COLD —
 * memories are evicted in this sim, and a trail can genuinely die).
 */
export function answerLead(m: TalkMemory, lead: Lead, f: ThreadFacts): { mem: TalkMemory; outcome: LeadOutcome; line: string } {
  const saw = f.witnessFacts.find((w) => w.factKey === lead.factKey);
  const heard = f.rumorFacts.find((r) => r.factKey === lead.factKey);
  const upd = (patch: Partial<Lead>): TalkMemory => ({ ...m, leads: m.leads.map((l) => (l.id === lead.id ? { ...l, ...patch } : l)) });
  if (saw) {
    let mem = upd({ state: 'resolved', trail: [...lead.trail, f.name] });
    mem = addTale(mem, { text: saw.text, from: f.name, fromId: f.versonaId, fromTown: f.town, day: f.nowDay, firstHand: true });
    const via = lead.trail.length > 1 ? `${lead.trail.length} mouths` : 'one mouth';
    return { mem, outcome: 'resolved', line: fillLine(`That? I was THERE. ${saw.text} — that\'s how it went, whatever shape it\'s taken since. It reached you through ${via}, did it? It does grow in the telling.`, f) };
  }
  if (heard && !heard.planted && heard.tellerId !== '0' && heard.tellerName !== '' && !lead.trail.includes(heard.tellerName)) {
    const mem = upd({ targetId: heard.tellerId, targetName: heard.tellerName, targetTown: heard.tellerTown, trail: [...lead.trail, f.name] });
    return { mem, outcome: 'advanced', line: `I didn\'t see it myself. ${sourceLine(heard, f)} If you want it from the root, ${heard.tellerName} is who you ask next.` };
  }
  const mem = upd({ state: 'cold', trail: [...lead.trail, f.name] });
  const why = heard ? 'It was just talk by the time it got to me — nobody\'s name attached.' : 'Did I say that? It\'s gone clean out of my head. A lot has happened since.';
  return { mem, outcome: 'cold', line: `${why} I think that trail ends with me, friend.` };
}

// ─── Life story: chapters from the versona's real facts ──────────────────────

export interface Chapter { key: string; minTier: (typeof TIER_ORDER)[number]; say: string }

/** The chapters THIS versona can tell, in telling order — built only from facts they actually have. */
export function lifeChapters(f: ThreadFacts): Chapter[] {
  const out: Chapter[] = [];
  if (f.bornTown && f.migrant) out.push({ key: 'roots', minTier: 'stranger', say: `I wasn\'t born here, you know. %born% is where I started — ${f.civDemonym || 'plain'} stock, the lot of us. I came to %town% for the work and stayed for reasons I still couldn\'t list for you. You never quite stop being from somewhere else.` });
  else if (f.town) out.push({ key: 'roots', minTier: 'stranger', say: 'Born in %town%, raised in %town%, and I expect they\'ll bury me in %town%. People say that like it\'s a small life. I say I know every stone of one place, which is more than most can claim of anywhere.' });
  if (f.job) out.push({ key: 'trade', minTier: 'stranger', say: f.title ? `They call me a ${f.title} now, which is a grander word than the work deserves. It\'s %job% work at %workplace% — I fell into it young and found I had the hands for it. There are worse reasons to spend a life on something.` : 'The work? %job%, at %workplace%. Nobody dreams of it as a child. But it turns out being needed is most of what a person wants, and %town% needs what I do.' });
  if (f.familyLine) out.push({ key: 'household', minTier: 'familiar', say: f.bondLine ? 'We\'re %family%. And — %bond% That\'s the person I\'d cross the settlement at midnight for. Everyone should have one.' : 'We\'re %family%. A household is a loud, expensive, exhausting thing, and I would not trade mine for a ship of my own.' });
  const hard = [...f.witnessFacts].sort((a, b) => b.importance - a.importance)[0];
  if (hard && hard.importance >= 6) out.push({ key: 'hard', minTier: 'familiar', say: `There\'s a thing I don\'t tell strangers. ${hard.text} I saw it myself. You carry a day like that in your body afterward — it\'s there when you lift things.` });
  if (f.atWar || f.warCasualties > 0) out.push({ key: 'war', minTier: 'familiar', say: f.warWeary ? 'This war with %foe%. %casualties% of ours gone, and for what — a line on somebody\'s chart? I was for it, at the start. I\'ll say that to you because I can\'t say it at the council table.' : 'The war with %foe% — I won\'t pretend I lie awake over it. They pushed; we pushed back. But I notice I count heads at the canteen now, which I never used to do.' });
  if (f.aspirationLine) out.push({ key: 'dream', minTier: 'friend', say: 'You want to know what I actually want? %aspiration% There. I\'ve told almost nobody that. Don\'t laugh — or do, but do it kindly.' });
  if (f.diaryLine || f.thought) out.push({ key: 'confidence', minTier: 'confidant', say: f.diaryLine ? 'I\'ll tell you what I wrote down last night, because you\'re the only one I would: "%diary%." I don\'t know what to do with that yet. Maybe nothing. It helped to say it aloud.' : 'Here\'s what goes round my head when the settlement\'s asleep: %thought%. I\'ve not said that to anyone. Keep it for me.' });
  return out;
}

export type ChapterResult =
  | { kind: 'tell'; chapter: Chapter; index: number; say: string }
  | { kind: 'not-today' | 'need-trust' | 'done' | 'nothing'; say: string };

/** "Tell me about yourself" — ONE chapter a visit-day; the next one may need more trust. */
export function nextChapter(f: ThreadFacts, mem: TalkMemory): ChapterResult {
  const chapters = lifeChapters(f);
  if (chapters.length === 0) return { kind: 'nothing', say: 'There\'s not much to tell, honestly. Ask me about the work instead.' };
  const me = talkOf(mem, f.versonaId);
  if (me.chapter >= chapters.length) return { kind: 'done', say: fillLine('You\'ve had the whole of it now, %player% — more than most people in %town% ever asked for. The rest I\'m still living.', f) };
  if (me.lastChapterDay === f.nowDay) return { kind: 'not-today', say: 'That\'s enough of me for one day. A life takes a while to tell — come back tomorrow and I\'ll pick up where I stopped.' };
  const ch = chapters[me.chapter];
  if (tierRank(f.relTier) < tierRank(ch.minTier)) {
    const cure = ch.minTier === 'familiar' ? 'Come by a few more times. I don\'t hand the rest to people I\'ve barely met.'
      : ch.minTier === 'friend' ? 'The next part I keep for friends. We\'re not there yet — but we might be.'
        : 'What\'s left I\'ve told no one. Maybe one day, if you\'re still around and still asking.';
    return { kind: 'need-trust', say: cure };
  }
  return { kind: 'tell', chapter: ch, index: me.chapter, say: fillLine(ch.say.replace(/%born%/g, f.bornTown), f) };
}
/** How you answer a chapter — three registers, so stance matters here too. */
export const CHAPTER_REPLIES: Reply[] = [
  { label: 'That can\'t have been easy.', tone: 'warm', effects: [{ t: 'chapter' }] },
  { label: 'Go on — I\'m listening.', tone: 'plain', effects: [{ t: 'chapter' }] },
  { label: 'And yet here you are.', tone: 'blunt', effects: [{ t: 'chapter' }] },
];

// ─── Fills specific to threads ───────────────────────────────────────────────

const PERSONA_WORDS: Record<PersonaSlot, Record<string, string>> = {
  origin: { spacer: 'ship-born', commons: 'from the Commons', frontier: 'off the frontier', secret: 'from nowhere you\'d say' },
  trade: { trader: 'a trader', pilot: 'a pilot', fighter: 'a fighter', crafter: 'a maker of things', wanderer: 'a wanderer' },
  purpose: { work: 'after work', curiosity: 'just looking around', trouble: 'looking for trouble', someone: 'looking for someone' },
  warView: { hawk: 'for the fight', dove: 'against the fight', neutral: 'on nobody\'s side' },
  rivalView: { open: 'easy about the other folk', wary: 'wary of the other folk' },
  townView: { fond: 'fond of the place', plain: 'neither here nor there about the place', sour: 'sour on the place' },
};
export function personaWord(slot: PersonaSlot, value: string): string { return PERSONA_WORDS[slot][value] ?? value; }

/** Thread fills: everything the topic system fills, plus what the threads know. */
export function fillThread(line: string, f: ThreadFacts, mem: TalkMemory): string {
  const me = talkOf(mem, f.versonaId);
  let s = line.replace(/%born%/g, f.bornTown || 'elsewhere').replace(/%title%/g, f.title || f.job);
  for (const slot of Object.keys(PERSONA_VALUES) as PersonaSlot[]) {
    const told = me.told[slot];
    s = s.replace(new RegExp(`%told:${slot}%`, 'g'), told ? personaWord(slot, told) : '');
    const heard = heardAbout(f, mem, slot);
    s = s.replace(new RegExp(`%heard:${slot}%`, 'g'), heard ? personaWord(slot, heard.value) : '')
      .replace(new RegExp(`%heardby:${slot}%`, 'g'), heard ? heard.byName : '');
  }
  return fillLine(s, f);
}
